import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import { HttpError } from '@revelio/data-access';

/** `requestId` is the `request-id` the call sent, which dash-meta logs. */
export class DashMetaHttpError extends HttpError {
  constructor(
    message: string,
    status: number,
    statusText: string,
    public readonly requestId: string
  ) {
    super(message, status, statusText);
    this.name = 'DashMetaHttpError';
  }
}

export async function dashMetaFetch(
  path: string,
  init?: RequestInit
): Promise<Response> {
  // Merged field by field and applied after `...init`, so caller headers
  // override a default without dropping the rest, CSRF token included.
  const headers = new Headers({
    'x-surf-token-dash-meta': getAuthDashMetaCsrfToken() ?? '',
    'request-id': crypto.randomUUID(),
  });
  // A FormData body needs the multipart type, with its boundary, that the
  // browser sets itself.
  if (!(init?.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  new Headers(init?.headers).forEach((value, name) => {
    headers.set(name, value);
  });

  // initAuth assigns DASH_META_ROOT after this module has loaded, so it is
  // read per call; a URL built at load time would freeze ''.
  return fetch(`${DASH_META_ROOT}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });
}

export async function dashMetaJson<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  // Set here rather than left to dashMetaFetch so the error can name it.
  const headers = new Headers(init?.headers);
  const requestId = headers.get('request-id') ?? crypto.randomUUID();
  headers.set('request-id', requestId);

  const response = await dashMetaFetch(path, { ...init, headers });
  const text = await response.text();

  if (!response.ok) {
    throw new DashMetaHttpError(
      text || 'Unknown error',
      response.status,
      response.statusText,
      requestId
    );
  }

  // A 204, and some 200/201s, have no body, which `response.json()` rejects.
  return (text ? JSON.parse(text) : undefined) as T;
}
